/**
 * ElementsDrawer.tsx
 * Drawer pour la bibliothèque d'éléments (formes, stickers, etc.)
 * Extrait de ModernSidebar.tsx
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAppStore } from '../../../../services/StateService';
import { createDefaultShape } from '../../../../types/ShapeTypes';
import { ElementLibraryService } from '../../../../services/ElementLibraryService';
import { useToast } from '../../common/ToastProvider';
import { logger } from '../../../../utils/ProfessionalLogger';

interface ElementsDrawerProps {
  onClose: () => void;
}

export const ElementsDrawer: React.FC<ElementsDrawerProps> = ({ onClose }) => {
  const addShape = useAppStore(state => state.addShape);
  interface ElementItem {
    id: string;
    name: string;
    type: string;
    url?: string;
    thumbnail?: string;
    isCustom?: boolean;
    [key: string]: unknown;
  }
  const [elements, setElements] = useState<ElementItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { addToast } = useToast();
  const elementLibraryService = ElementLibraryService.getInstance();

  useEffect(() => {
    loadElements();
  }, [selectedCategory]);

  const loadElements = async () => {
    try {
      setLoading(true);
      // P3 : la signature de getElements attend un sous-ensemble strict de catégories.
      // 'all' / 'lines' tolérés via cast (le service filtrera).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const category = selectedCategory === 'all' ? undefined : (selectedCategory as any);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const allElements = await elementLibraryService.getElements(category as any);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setElements(allElements as any);
    } catch (error) {
      logger.error('Erreur lors du chargement des éléments:', error);
      addToast({
        type: 'error',
        message: 'Erreur lors du chargement des éléments',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddElement = useCallback(
    (element: ElementItem) => {
      logger.info(`[ElementsDrawer] handleAddElement appelé avec element:`, element);
      try {
        // FIX (éditeur type Canva) : les formes géométriques de base doivent être
        // des shapes NATIVES Konva (recolorables, redimensionnables) et non des
        // images SVG figées. On mappe par nom vers un type natif. Seuls les
        // éléments sans équivalent natif (flèches, badges, bulles) restent images.
        const name = (element.name || '').toLowerCase();
        const nativeType: 'rect' | 'circle' | 'triangle' | 'star' | 'heart' | null = name.includes(
          'rectangle'
        )
          ? 'rect'
          : name.includes('cercle') || name.includes('circle')
            ? 'circle'
            : name.includes('triangle')
              ? 'triangle'
              : name.includes('étoile') || name.includes('etoile') || name.includes('star')
                ? 'star'
                : name.includes('cœur') || name.includes('coeur') || name.includes('heart')
                  ? 'heart'
                  : null;

        if (nativeType) {
          // Centrer dans le document (toujours visible quel que soit le zoom).
          const { canvas } = useAppStore.getState();
          const docW = canvas.templateWidth || 800;
          const docH = canvas.templateHeight || 600;
          const shape = createDefaultShape(nativeType, 100, 100);
          const w = shape.width ?? 150;
          const h = shape.height ?? 100;
          shape.x = Math.max(0, Math.round((docW - w) / 2));
          shape.y = Math.max(0, Math.round((docH - h) / 2));
          addShape(shape);
          logger.info(`[ElementsDrawer] Shape native ${nativeType} ajoutée: ${shape.id}`);
        } else {
          // Élément graphique (flèche, badge, bulle…) → image SVG.
          const { canvas } = useAppStore.getState();
          const docW = canvas.templateWidth || 800;
          const docH = canvas.templateHeight || 600;
          const shape = createDefaultShape('image', 100, 100);
          shape.src = element.url;
          shape.width = 200;
          shape.height = 200;
          shape.x = Math.max(0, Math.round((docW - 200) / 2));
          shape.y = Math.max(0, Math.round((docH - 200) / 2));
          addShape(shape);
          logger.info(`[ElementsDrawer] Image ajoutée: ${shape.id}`);
        }
        const store = useAppStore.getState();
        logger.info(`[ElementsDrawer] Nombre de shapes dans le store: ${store.shapes.length}`);
        onClose();
      } catch (error) {
        logger.error("[ElementsDrawer] Erreur lors de l'ajout de l'élément:", error);
        addToast({ type: 'error', message: "Erreur lors de l'ajout de l'élément", duration: 5000 });
      }
    },
    [addShape, onClose, addToast]
  );

  const handleImportElement = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = [
      'image/svg+xml',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'application/postscript',
    ];
    const validExtensions = ['.svg', '.png', '.jpg', '.jpeg', '.eps'];
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      addToast({
        type: 'error',
        message: 'Format de fichier non supporté. Utilisez SVG, PNG, JPG ou EPS.',
        duration: 5000,
      });
      return;
    }

    try {
      setUploading(true);
      const _element = await elementLibraryService.addCustomElement(file);
      addToast({ type: 'success', message: 'Élément importé avec succès', duration: 3000 });
      loadElements();
    } catch (error) {
      logger.error("Erreur lors de l'import:", error);
      addToast({ type: 'error', message: "Erreur lors de l'import de l'élément", duration: 5000 });
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const categories = [
    { id: 'all', label: 'Tous' },
    { id: 'shapes', label: 'Formes' },
    { id: 'arrows', label: 'Flèches' },
    { id: 'badges', label: 'Badges' },
    { id: 'bubbles', label: 'Bulles' },
    { id: 'custom', label: 'Mes éléments' },
  ];

  return (
    <>
      <div className="drawer-header">
        <h3 className="drawer-title">Éléments</h3>
        <button className="drawer-close-btn" onClick={onClose} aria-label="Fermer">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className="drawer-content">
        <div style={{ marginBottom: 'var(--spacing-md)' }}>
          <label
            style={{
              display: 'block',
              width: '100%',
              padding: 'var(--spacing-sm) var(--spacing-md)',
              background: 'var(--color-accent, #3b82f6)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: uploading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              textAlign: 'center',
              opacity: uploading ? 0.6 : 1,
            }}
          >
            <input
              type="file"
              accept=".svg,.png,.jpg,.jpeg,.eps"
              onChange={handleImportElement}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            {uploading ? '⏳ Import en cours...' : '📤 Importer un élément'}
          </label>
        </div>

        <div
          style={{
            marginBottom: 'var(--spacing-md)',
            display: 'flex',
            gap: 'var(--spacing-xs)',
            flexWrap: 'wrap',
          }}
        >
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                padding: 'var(--spacing-xs) var(--spacing-sm)',
                background:
                  selectedCategory === cat.id
                    ? 'var(--color-accent, #3b82f6)'
                    : 'var(--color-bg-secondary)',
                color: selectedCategory === cat.id ? 'white' : 'var(--color-text-primary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s',
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div
            style={{
              textAlign: 'center',
              padding: 'var(--spacing-xl)',
              color: 'var(--color-text-secondary)',
            }}
          >
            Chargement...
          </div>
        ) : elements.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: 'var(--spacing-xl)',
              color: 'var(--color-text-secondary)',
            }}
          >
            Aucun élément dans cette catégorie
          </div>
        ) : (
          <div className="drawer-grid">
            {elements.map(element => (
              <div
                key={element.id}
                className="drawer-item"
                draggable={true}
                onDragStart={e => {
                  // Format standardisé pour le drag & drop
                  e.dataTransfer.effectAllowed = 'copy';

                  // Déterminer le type de shape
                  let shapeType: string;
                  if (element.type === 'svg' || element.type === 'png' || element.type === 'jpg') {
                    shapeType = 'image';
                  } else {
                    // Déterminer le type selon le nom
                    const name = element.name.toLowerCase();
                    if (name.includes('rectangle')) {
                      shapeType = 'rect';
                    } else if (name.includes('cercle') || name.includes('circle')) {
                      shapeType = 'circle';
                    } else if (name.includes('triangle')) {
                      shapeType = 'triangle';
                    } else if (name.includes('étoile') || name.includes('star')) {
                      shapeType = 'star';
                    } else if (name.includes('cœur') || name.includes('heart')) {
                      shapeType = 'heart';
                    } else {
                      shapeType = 'rect'; // Par défaut
                    }
                  }

                  // Utiliser le format standardisé
                  e.dataTransfer.setData('application/snapbooth-type', shapeType);

                  // Si c'est une image, passer l'URL dans le payload
                  if (shapeType === 'image' && element.url) {
                    e.dataTransfer.setData('application/snapbooth-payload', element.url);
                  }

                  logger.info(
                    `[ElementsDrawer] Élément drag start: ${element.name} (type: ${shapeType})`
                  );
                }}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  logger.info(`[ElementsDrawer] Élément cliqué: ${element.name}`);
                  handleAddElement(element);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    logger.info(`[ElementsDrawer] Élément activé (clavier): ${element.name}`);
                    handleAddElement(element);
                  }
                }}
                role="button"
                tabIndex={0}
                style={{
                  cursor: 'grab',
                  position: 'relative',
                  zIndex: 1,
                  pointerEvents: 'auto',
                }}
              >
                <div
                  className="drawer-item-preview"
                  style={{
                    background: element.isCustom
                      ? 'var(--color-bg-canvas, #333333)'
                      : `linear-gradient(135deg, var(--color-accent-light, rgba(59, 130, 246, 0.1)) 0%, var(--color-accent-light, rgba(59, 130, 246, 0.2)) 100%)`,
                    borderColor: 'var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {element.type === 'svg' || element.type === 'png' || element.type === 'jpg' ? (
                    <img
                      src={element.thumbnail || element.url}
                      alt={element.name}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <span style={{ fontSize: '32px' }}>📄</span>
                  )}
                </div>
                <p className="drawer-item-title">{element.name}</p>
                {element.isCustom && (
                  <span style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>
                    Personnel
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
